import { usePagination } from './usePagination'
import { RootState } from '@/redux/reducers'
import { PayloadFetchTickets } from '@/redux/saga/fetchGameListSaga'
import { sagaActions } from '@/redux/sagaActions'
import { TicketStatusCardProps } from '@/ui-components/components/TicketStatusCard/TicketStatusCard.types'
import { getAsTicketStatus } from '@/ui-components/components/TicketStatusCard/utils'
import {
  convertToDateTime,
  DATE_TIME_FORMAT_HUMAN,
} from '@/utils/date-time-utils'
import { useCallback, useEffect, useMemo } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router'
import { useHandleCancel } from './useHandleCancel'
import { createSelector } from '@reduxjs/toolkit'
import { useGameUtils } from '@/utils/gameUtils'
import { useDialog } from '@/ui-components/components/ConfirmDialog/useDialog'

export enum TabType {
  RECENT = 'waiting,approved',
  RESULTED = 'settled',
  CANCELLED = 'cancelled,rejected',
  HISTORY = 'history',
}

// Memoized selector outside component
const selectPollTabData = createSelector(
  [
    (state: RootState) => state.games.response,
    (state: RootState) => state.games.listTicket,
    (state: RootState) => state.games.listTicket?.count ?? 0,
    (state: RootState) => state.games.loading.listTicket,
  ],
  (response, listTicket, count, loading) => ({
    response,
    listTicket,
    count,
    loading,
  }),
)

const usePollTab = (ticketType: TabType) => {
  const { onCancel, checkCanCancel } = useHandleCancel()
  const dispatch = useDispatch()
  const navigation = useNavigate()
  const { page, limit, onPageChange, resetPage } = usePagination(
    'created_at DESC',
    10,
  )
  const { t, i18n } = useTranslation()
  const { getDetailName, getGameName } = useGameUtils()
  const dialog = useDialog(t)
  const handleCancel = useCallback(
    (ticketId: number) => {
      dialog.confirm(
        t('message.confirmCancelBet', { pollNumber: ticketId.toString() }),
        {
          onConfirm: () => onCancel(ticketId),
        },
      )
    },
    [dialog, t, onCancel],
  )

  // Use memoized selector
  const { response, listTicket, count, loading } =
    useSelector(selectPollTabData)

  const fetchData = useCallback(() => {
    const payload: PayloadFetchTickets = {
      page: page ?? 1,
      pageSize: limit,
      ticketType,
    }
    if (ticketType !== TabType.HISTORY) {
      dispatch({ type: sagaActions.FETCH_TICKET_PAGINATION, payload })
    } else {
      dispatch({ type: sagaActions.FETCH_TICKET_PAGINATION_HISTORY, payload })
    }
    dispatch({ type: sagaActions.CLEAR_RESPONSE })
  }, [dispatch, page, limit, ticketType])

  useEffect(() => {
    if (response?.message && response.status === 'OK') {
      toast.success(
        t('message.successCancelBet', { pollNumber: response?.data?.id }),
      )
      dispatch({ type: sagaActions.CLEAR_RESPONSE })
    }
  }, [response, dispatch, t])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    resetPage()
  }, [ticketType, resetPage])

  const tickets = useMemo(
    () =>
      listTicket?.tickets?.map((ticket) => {
        const canCancel = checkCanCancel(ticket)
        const item: TicketStatusCardProps = {
          ticketNumber: ticket.id.toString(),
          betDate: convertToDateTime(
            ticket.created_at,
            DATE_TIME_FORMAT_HUMAN,
            i18n.language,
          ),
          listTitle: getGameName(ticket.lotto_game),
          drawDate: getDetailName(ticket.lotto_game, ticket.lotto_round_game),
          amount: ticket.bet_amount,
          winAmount: ticket.win_amount,
          status: getAsTicketStatus(ticket.status, ticket?.cancellation_reason), // Resolves the final TicketStatus with cancellation reason
          bonus: ticket.bonus,
          isPrimary: ticket.is_primary,
          onViewClick: () => {
            if (ticketType === TabType.HISTORY) {
              navigation(`history/${ticket.id}`)
              return
            }
            navigation(`${ticket.id}`)
          },
          canCancel,
        }
        if (ticketType != TabType.RESULTED && ticketType != TabType.HISTORY) {
          item.onCancel = () => {
            handleCancel(ticket.id)
          }
        }
        return item
      }) ?? [],
    [
      listTicket?.tickets,
      checkCanCancel,
      i18n.language,
      ticketType,
      navigation,
      getDetailName,
      getGameName,
      handleCancel,
    ],
  )

  return {
    tickets,
    currentPage: page,
    totalPages: Math.ceil(count / limit),
    onPageChange,
    loading,
    dialogComponent: dialog.render(),
  }
}

export default usePollTab
